import { db, functions } from '../firebase/config';
import {
  collection,
  query,
  getDocs,
  onSnapshot,
  orderBy,
  where
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

const isDev = process.env.NODE_ENV === 'development';
const devLog = (...args) => isDev && console.log(...args);

/**
 * Permisos del dashboard por defecto para roles del sistema
 */
export const DEFAULT_PERMISSIONS = {
  dashboard: false,
  customerExperience: false,
  accountManagement: false,
  staffAmplifier: false,
  kitchenMatrix: false,
  mixologyLab: false,
  clients: false,
  reviews: false,
  rewards: false,
  billing: false,
  tableManagement: false,
  integrations: false,
  collaborators: false,
  settings: false,
  reservations: false,
  marketing: false,
  inventoryManagement: false,
  inventoryKitchen: false,
  inventoryBar: false
};

/**
 * Roles del sistema con permisos predefinidos
 */
export const SYSTEM_ROLES = {
  owner: {
    id: 'owner',
    name: 'Dueño del Restaurante',
    iconId: 'crown',
    colorId: 'yellow',
    description: 'Acceso completo al sistema. No puede ser modificado.',
    isSystem: true,
    canBeAssigned: false,
    permissions: {
      dashboard: true, customerExperience: true, accountManagement: true,
      staffAmplifier: true, kitchenMatrix: true, mixologyLab: true,
      clients: true, reviews: true, rewards: true, billing: true,
      tableManagement: true, integrations: true, collaborators: true, settings: true,
      reservations: true, marketing: true,
      inventoryManagement: true, inventoryKitchen: true, inventoryBar: true
    }
  },
  admin: {
    id: 'admin',
    name: 'Administrador',
    iconId: 'shield-check',
    colorId: 'purple',
    description: 'Gestión completa del restaurante excepto configuración de roles.',
    isSystem: true,
    canBeAssigned: true,
    permissions: {
      dashboard: true, customerExperience: true, accountManagement: true,
      staffAmplifier: true, kitchenMatrix: true, mixologyLab: true,
      clients: true, reviews: true, rewards: true, billing: true,
      tableManagement: true, integrations: true, collaborators: true, settings: false,
      reservations: true, marketing: true,
      inventoryManagement: true, inventoryKitchen: true, inventoryBar: true
    }
  },
  cajero: {
    id: 'cajero',
    name: 'Cajero',
    iconId: 'credit-card',
    colorId: 'green',
    description: 'Gestión de cuentas, pagos y cierre de caja.',
    isSystem: true,
    canBeAssigned: true,
    permissions: {
      dashboard: true, customerExperience: true, accountManagement: true,
      staffAmplifier: false, kitchenMatrix: false, mixologyLab: false,
      clients: false, reviews: false, rewards: false, billing: false,
      tableManagement: false, integrations: false, collaborators: false, settings: false,
      reservations: false, marketing: false,
      inventoryManagement: false, inventoryKitchen: false, inventoryBar: false
    }
  },
  mesero: {
    id: 'mesero',
    name: 'Mesero',
    iconId: 'users',
    colorId: 'blue',
    description: 'Toma de pedidos y atención de mesas.',
    isSystem: true,
    canBeAssigned: true,
    permissions: {
      dashboard: true, customerExperience: true, accountManagement: false,
      staffAmplifier: false, kitchenMatrix: false, mixologyLab: false,
      clients: false, reviews: false, rewards: false, billing: false,
      tableManagement: true, integrations: false, collaborators: false, settings: false,
      reservations: false, marketing: false,
      inventoryManagement: false, inventoryKitchen: false, inventoryBar: false
    }
  },
  cocina: {
    id: 'cocina',
    name: 'Cocina',
    iconId: 'chef-hat',
    colorId: 'orange',
    description: 'Vista de pedidos pendientes y gestión de cocina.',
    isSystem: true,
    canBeAssigned: true,
    permissions: {
      dashboard: false, customerExperience: false, accountManagement: false,
      staffAmplifier: false, kitchenMatrix: true, mixologyLab: false,
      clients: false, reviews: false, rewards: false, billing: false,
      tableManagement: false, integrations: false, collaborators: false, settings: false,
      reservations: false, marketing: false,
      inventoryManagement: false, inventoryKitchen: true, inventoryBar: false
    }
  }
};

class TeamService {
  constructor() {
    this.memoryCache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutos
    this.activeSubscriptions = new Map();
  }

  getCacheKey(restaurantId, identifier) {
    const timeSlot = Math.floor(Date.now() / this.cacheTimeout);
    return `team_${restaurantId}_${identifier}_${timeSlot}`;
  }

  // ==================== TEAM MEMBERS ====================

  /**
   * Obtener todos los miembros del equipo
   */
  async getTeamMembers(restaurantId) {
    if (!restaurantId) {
      throw new Error('Restaurant ID is required');
    }

    const cacheKey = this.getCacheKey(restaurantId, 'members');

    if (this.memoryCache.has(cacheKey)) {
      devLog('Team members data from cache');
      return this.memoryCache.get(cacheKey);
    }

    try {
      devLog(`Fetching team members for restaurant: ${restaurantId}`);

      const membersRef = collection(db, 'restaurants', restaurantId, 'teamMembers');
      const q = query(membersRef, orderBy('name', 'asc'));
      const snapshot = await getDocs(q);

      const members = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        joinedAt: doc.data().joinedAt?.toDate() || null,
        lastAccess: doc.data().lastAccess?.toDate() || null,
        updatedAt: doc.data().updatedAt?.toDate() || null
      }));

      devLog(`Team members fetched: ${members.length}`);

      this.memoryCache.set(cacheKey, members);
      return members;
    } catch (error) {
      devLog('❌ Error fetching team members:', error);
      throw error;
    }
  }

  /**
   * Suscripción en tiempo real a los miembros del equipo
   */
  subscribeToTeamMembers(restaurantId, callback) {
    if (!restaurantId) {
      devLog('Restaurant ID is required for subscription');
      return null;
    }

    const subscriptionKey = `team_members_${restaurantId}`;

    if (this.activeSubscriptions.has(subscriptionKey)) {
      this.activeSubscriptions.get(subscriptionKey)();
    }

    try {
      devLog('Setting up real-time team members subscription');

      const membersRef = collection(db, 'restaurants', restaurantId, 'teamMembers');
      const q = query(membersRef, orderBy('name', 'asc'));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        try {
          const members = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            joinedAt: doc.data().joinedAt?.toDate() || null,
            lastAccess: doc.data().lastAccess?.toDate() || null,
            updatedAt: doc.data().updatedAt?.toDate() || null
          }));

          devLog(`Real-time team members update: ${members.length}`);

          const cacheKey = this.getCacheKey(restaurantId, 'members');
          this.memoryCache.set(cacheKey, members);

          callback(members, null);
        } catch (error) {
          devLog('Error processing real-time team members update:', error);
          callback(null, error);
        }
      }, (error) => {
        devLog('Error in team members subscription:', error);
        callback(null, error);
      });

      this.activeSubscriptions.set(subscriptionKey, unsubscribe);
      return unsubscribe;
    } catch (error) {
      devLog('Error setting up team members subscription:', error);
      callback(null, error);
      return null;
    }
  }

  /**
   * Actualizar miembro del equipo
   */
  async updateTeamMember(memberId, roleId, customPermissions = null, restaurantId = null) {
    try {
      devLog(`📝 Updating team member: ${memberId}`);

      const manageTeamMemberFn = httpsCallable(functions, 'restaurants-endpoints-manageTeamMember');
      const result = await manageTeamMemberFn({
        action: 'update',
        memberId,
        roleId,
        customPermissions,
        ...(restaurantId && { restaurantId })
      });

      devLog('✅ Team member updated:', result.data);
      this.clearCache();
      return result.data;
    } catch (error) {
      devLog('❌ Error updating team member:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Eliminar miembro del equipo
   */
  async removeTeamMember(memberId, restaurantId = null) {
    try {
      devLog(`🗑️ Removing team member: ${memberId}`);

      const manageTeamMemberFn = httpsCallable(functions, 'restaurants-endpoints-manageTeamMember');
      const result = await manageTeamMemberFn({
        action: 'remove',
        memberId,
        ...(restaurantId && { restaurantId })
      });

      devLog('✅ Team member removed:', result.data);
      this.clearCache();
      return result.data;
    } catch (error) {
      devLog('❌ Error removing team member:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Suspender miembro del equipo
   */
  async suspendTeamMember(memberId, restaurantId = null) {
    try {
      devLog(`⏸️ Suspending team member: ${memberId}`);

      const manageTeamMemberFn = httpsCallable(functions, 'restaurants-endpoints-manageTeamMember');
      const result = await manageTeamMemberFn({
        action: 'suspend',
        memberId,
        ...(restaurantId && { restaurantId })
      });

      devLog('✅ Team member suspended:', result.data);
      this.clearCache();
      return result.data;
    } catch (error) {
      devLog('❌ Error suspending team member:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Reactivar miembro del equipo
   */
  async reactivateTeamMember(memberId, restaurantId = null) {
    try {
      devLog(`▶️ Reactivating team member: ${memberId}`);

      const manageTeamMemberFn = httpsCallable(functions, 'restaurants-endpoints-manageTeamMember');
      const result = await manageTeamMemberFn({
        action: 'reactivate',
        memberId,
        ...(restaurantId && { restaurantId })
      });

      devLog('✅ Team member reactivated:', result.data);
      this.clearCache();
      return result.data;
    } catch (error) {
      devLog('❌ Error reactivating team member:', error);
      throw this.handleError(error);
    }
  }

  // ==================== INVITATIONS ====================

  /**
   * Obtener invitaciones pendientes
   */
  async getPendingInvitations(restaurantId) {
    if (!restaurantId) {
      throw new Error('Restaurant ID is required');
    }

    const cacheKey = this.getCacheKey(restaurantId, 'invitations');

    if (this.memoryCache.has(cacheKey)) {
      devLog('📦 Invitations data from cache');
      return this.memoryCache.get(cacheKey);
    }

    try {
      devLog(`📧 Fetching pending invitations for restaurant: ${restaurantId}`);

      const invitationsRef = collection(db, 'restaurants', restaurantId, 'invitations');
      const q = query(
        invitationsRef, 
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);

      const invitations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || null,
        expiresAt: doc.data().expiresAt?.toDate() || null
      }));

      devLog(`✅ Pending invitations fetched: ${invitations.length}`);

      this.memoryCache.set(cacheKey, invitations);
      return invitations;
    } catch (error) {
      devLog('❌ Error fetching invitations:', error);
      throw error;
    }
  }

  /**
   * Suscripción en tiempo real a las invitaciones
   */
  subscribeToInvitations(restaurantId, callback) {
    if (!restaurantId) {
      devLog('Restaurant ID is required for subscription');
      return null;
    }

    const subscriptionKey = `invitations_${restaurantId}`;

    if (this.activeSubscriptions.has(subscriptionKey)) {
      this.activeSubscriptions.get(subscriptionKey)();
    }

    try {
      devLog('🔄 Setting up real-time invitations subscription');

      const invitationsRef = collection(db, 'restaurants', restaurantId, 'invitations');
      const q = query(
        invitationsRef,
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        try {
          const invitations = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || null,
            expiresAt: doc.data().expiresAt?.toDate() || null
          }));

          devLog(`📊 Real-time invitations update: ${invitations.length}`);

          const cacheKey = this.getCacheKey(restaurantId, 'invitations');
          this.memoryCache.set(cacheKey, invitations);

          callback(invitations, null);
        } catch (error) {
          devLog('❌ Error processing real-time invitations update:', error);
          callback(null, error);
        }
      }, (error) => {
        devLog('❌ Error in invitations subscription:', error);
        callback(null, error);
      });

      this.activeSubscriptions.set(subscriptionKey, unsubscribe);
      return unsubscribe;
    } catch (error) {
      devLog('❌ Error setting up invitations subscription:', error);
      callback(null, error);
      return null;
    }
  }

  /**
   * Enviar invitación
   */
  async sendInvitation(email, roleId, customPermissions = null, restaurantId = null) {
    try {
      devLog(`📧 Sending invitation to: ${email}`);

      const sendTeamInvitationFn = httpsCallable(functions, 'restaurants-endpoints-sendTeamInvitation');
      const result = await sendTeamInvitationFn({
        email,
        roleId,
        customPermissions,
        ...(restaurantId && { restaurantId })
      });

      devLog('✅ Invitation sent:', result.data);
      this.clearCache();
      return result.data;
    } catch (error) {
      devLog('❌ Error sending invitation:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Cancelar invitación
   */
  async cancelInvitation(invitationId, restaurantId = null) {
    try {
      devLog(`❌ Cancelling invitation: ${invitationId}`);

      const manageTeamInvitationFn = httpsCallable(functions, 'restaurants-endpoints-manageTeamInvitation');
      const result = await manageTeamInvitationFn({
        invitationId,
        action: 'cancel',
        ...(restaurantId && { restaurantId })
      });

      devLog('✅ Invitation cancelled:', result.data);
      this.clearCache();
      return result.data;
    } catch (error) {
      devLog('❌ Error cancelling invitation:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Reenviar invitación
   */
  async resendInvitation(invitationId, restaurantId = null) {
    try {
      devLog(`🔄 Resending invitation: ${invitationId}`);

      const manageTeamInvitationFn = httpsCallable(functions, 'restaurants-endpoints-manageTeamInvitation');
      const result = await manageTeamInvitationFn({
        invitationId,
        action: 'resend',
        ...(restaurantId && { restaurantId })
      });

      devLog('✅ Invitation resent:', result.data);
      this.clearCache();
      return result.data;
    } catch (error) {
      devLog('❌ Error resending invitation:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Aceptar invitación (para página de aceptación)
   */
  async acceptInvitation(token, name, password = null) {
    try {
      devLog(`✅ Accepting invitation with token`);

      const acceptTeamInvitationFn = httpsCallable(functions, 'restaurants-endpoints-acceptTeamInvitation');
      const result = await acceptTeamInvitationFn({
        token,
        name,
        password
      });

      devLog('✅ Invitation accepted:', result.data);
      return result.data;
    } catch (error) {
      devLog('❌ Error accepting invitation:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Verificar invitación por token (solo lectura)
   * Retorna info de la invitación y si el usuario ya existe
   */
  async verifyInvitationToken(token) {
    try {
      devLog(`🔍 Verifying invitation token: ${token?.substring(0, 8)}...`);

      const verifyFn = httpsCallable(functions, 'restaurants-endpoints-verifyInvitationToken');
      const result = await verifyFn({ token });

      devLog('✅ Token verification result:', result.data);
      return result.data;
    } catch (error) {
      devLog('❌ Error verifying token:', error);
      throw this.handleError(error);
    }
  }

  // ==================== ROLES ====================

  /**
   * Obtener roles del restaurante (sistema + personalizados)
   */
  async getRoles(restaurantId) {
    if (!restaurantId) {
      throw new Error('Restaurant ID is required');
    }

    const cacheKey = this.getCacheKey(restaurantId, 'roles');

    if (this.memoryCache.has(cacheKey)) {
      devLog('📦 Roles data from cache');
      return this.memoryCache.get(cacheKey);
    }

    try {
      devLog(`🎭 Fetching roles for restaurant: ${restaurantId}`);

      const rolesRef = collection(db, 'restaurants', restaurantId, 'roles');
      const q = query(rolesRef, orderBy('name', 'asc'));
      const snapshot = await getDocs(q);

      const customRoles = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || null,
        updatedAt: doc.data().updatedAt?.toDate() || null
      }));

      // Combinar roles del sistema con roles personalizados
      const systemRolesList = Object.values(SYSTEM_ROLES).filter(r => r.canBeAssigned);
      const allRoles = [...systemRolesList, ...customRoles];

      devLog(`✅ Roles fetched: ${allRoles.length} (${customRoles.length} custom)`);

      this.memoryCache.set(cacheKey, allRoles);
      return allRoles;
    } catch (error) {
      devLog('❌ Error fetching roles:', error);
      throw error;
    }
  }

  /**
   * Suscripción en tiempo real a los roles
   */
  subscribeToRoles(restaurantId, callback) {
    if (!restaurantId) {
      devLog('Restaurant ID is required for subscription');
      return null;
    }

    const subscriptionKey = `roles_${restaurantId}`;

    if (this.activeSubscriptions.has(subscriptionKey)) {
      this.activeSubscriptions.get(subscriptionKey)();
    }

    try {
      devLog('🔄 Setting up real-time roles subscription');

      const rolesRef = collection(db, 'restaurants', restaurantId, 'roles');
      const q = query(rolesRef, orderBy('name', 'asc'));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        try {
          const customRoles = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || null,
            updatedAt: doc.data().updatedAt?.toDate() || null
          }));

          const systemRolesList = Object.values(SYSTEM_ROLES).filter(r => r.canBeAssigned);
          const allRoles = [...systemRolesList, ...customRoles];

          devLog(`📊 Real-time roles update: ${allRoles.length}`);

          const cacheKey = this.getCacheKey(restaurantId, 'roles');
          this.memoryCache.set(cacheKey, allRoles);

          callback(allRoles, null);
        } catch (error) {
          devLog('❌ Error processing real-time roles update:', error);
          callback(null, error);
        }
      }, (error) => {
        devLog('❌ Error in roles subscription:', error);
        callback(null, error);
      });

      this.activeSubscriptions.set(subscriptionKey, unsubscribe);
      return unsubscribe;
    } catch (error) {
      devLog('❌ Error setting up roles subscription:', error);
      callback(null, error);
      return null;
    }
  }

  /**
   * Crear rol personalizado
   */
  async createRole(roleData, restaurantId = null) {
    try {
      devLog(`➕ Creating role: ${roleData.name}`);

      const manageRoleFn = httpsCallable(functions, 'restaurants-endpoints-manageRole');
      const result = await manageRoleFn({
        action: 'create',
        roleData,
        ...(restaurantId && { restaurantId })
      });

      devLog('✅ Role created:', result.data);
      this.clearCache();
      return result.data;
    } catch (error) {
      devLog('❌ Error creating role:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Actualizar rol personalizado
   */
  async updateRole(roleId, roleData, restaurantId = null) {
    try {
      devLog(`📝 Updating role: ${roleId}`);

      const manageRoleFn = httpsCallable(functions, 'restaurants-endpoints-manageRole');
      const result = await manageRoleFn({
        action: 'update',
        roleId,
        roleData,
        ...(restaurantId && { restaurantId })
      });

      devLog('✅ Role updated:', result.data);
      this.clearCache();
      return result.data;
    } catch (error) {
      devLog('❌ Error updating role:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Eliminar rol personalizado
   */
  async deleteRole(roleId, restaurantId = null) {
    try {
      devLog(`🗑️ Deleting role: ${roleId}`);

      const manageRoleFn = httpsCallable(functions, 'restaurants-endpoints-manageRole');
      const result = await manageRoleFn({
        action: 'delete',
        roleId,
        ...(restaurantId && { restaurantId })
      });

      devLog('✅ Role deleted:', result.data);
      this.clearCache();
      return result.data;
    } catch (error) {
      devLog('❌ Error deleting role:', error);
      throw this.handleError(error);
    }
  }

  // ==================== UTILITIES ====================

  /**
   * Obtener información de rol por ID
   */
  getRoleInfo(roleId) {
    // Primero buscar en roles del sistema
    if (SYSTEM_ROLES[roleId]) {
      return SYSTEM_ROLES[roleId];
    }
    return null;
  }

  /**
   * Formatear tiempo relativo
   */
  formatRelativeTime(date) {
    if (!date) return 'Nunca';
    
    const now = new Date();
    const diffMs = now - new Date(date);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins}m`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;
    return new Date(date).toLocaleDateString('es-CL');
  }

  /**
   * Calcular días hasta expiración
   */
  getDaysUntilExpiration(expiresAt) {
    if (!expiresAt) return 0;
    const now = new Date();
    const expDate = new Date(expiresAt);
    const diffDays = Math.ceil((expDate - now) / 86400000);
    return Math.max(0, diffDays);
  }

  /**
   * Manejo de errores de Cloud Functions
   */
  handleError(error) {
    const errorMessages = {
      'unauthenticated': 'Debes iniciar sesión para realizar esta acción.',
      'permission-denied': 'No tienes permisos para realizar esta acción.',
      'not-found': 'El recurso no fue encontrado.',
      'already-exists': error.message || 'El recurso ya existe.',
      'invalid-argument': error.message || 'Datos inválidos.',
      'failed-precondition': error.message || 'No se puede realizar esta acción.',
      'deadline-exceeded': 'La invitación ha expirado.',
      'internal': 'Error interno. Por favor intenta de nuevo.'
    };

    const code = error.code?.replace('functions/', '') || 'internal';
    const message = errorMessages[code] || error.message || 'Error desconocido';

    return new Error(message);
  }

  /**
   * Limpiar cache
   */
  clearCache() {
    devLog('🧹 Clearing team service cache');
    this.memoryCache.clear();
  }

  /**
   * Cancelar todas las suscripciones
   */
  unsubscribeAll() {
    devLog(`🔄 Unsubscribing from ${this.activeSubscriptions.size} subscriptions`);
    this.activeSubscriptions.forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch (error) {
        devLog('⚠️ Error unsubscribing:', error);
      }
    });
    this.activeSubscriptions.clear();
  }
}

export const teamService = new TeamService();
export default teamService;
